import { memo, useEffect } from 'react';

import { usePredictionOutcomeChance } from './usePredictionOutcomeChance';
import { usePredictionOutcomePrices } from './usePredictionOutcomePrices';

/** Market-data values for one outcome, as shown on a card row. */
export interface PredictionOutcomePriceSummary {
  /** Last traded chance %, falling back to the Yes book mid. Unrounded; round at display. */
  chancePct: number | undefined;
  /** Yes book ask — Yes button label and buy-Yes order prime. */
  yesAskPrice: string | undefined;
  /** Yes book bid — buy-No = 1 − bid for No button label and order prime. */
  yesBidPrice: string | undefined;
}

interface PredictionOutcomePriceSubscriberProps {
  symbol: string;
  subscriptionTag: string;
  marketAccounts?: string[];
  quantity?: string;
  onPricesChange: (symbol: string, prices: PredictionOutcomePriceSummary) => void;
}

/**
 * Headless subscriber that streams a single outcome's prices up to the card so the card can sort
 * every outcome by chance %, even ones that are paginated out of view. Renders nothing.
 */
export const PredictionOutcomePriceSubscriber = memo(function PredictionOutcomePriceSubscriber({
  symbol,
  subscriptionTag,
  marketAccounts,
  quantity,
  onPricesChange,
}: PredictionOutcomePriceSubscriberProps) {
  const { midChancePct, yesAskPrice, yesBidPrice } = usePredictionOutcomePrices(symbol, subscriptionTag, {
    marketAccounts,
    quantity,
  });
  const lastTradedChancePct = usePredictionOutcomeChance(symbol, `${subscriptionTag}-chance`, {
    marketAccounts,
  });
  const chancePct = lastTradedChancePct ?? midChancePct;

  useEffect(() => {
    onPricesChange(symbol, { chancePct, yesAskPrice, yesBidPrice });
  }, [symbol, onPricesChange, chancePct, yesAskPrice, yesBidPrice]);

  return null;
});
